import { useState } from "react";
import { useNavigate } from "react-router";

import {
  Calendar,
  ExternalLink,
  Eye,
  EyeOff,
  FlaskConical,
  Music,
  Users,
} from "lucide-react";

import {
  EnrichmentAsyncHint,
  shouldSuppressEnrichmentLink,
} from "@/components/common/enrichment-async-hint";
import { ARTIST_FEATURE_LABELS } from "@/components/enrichment/config";
import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";
import { ZoomablePhoto } from "@/components/media/photo-zoomable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { LinkSpotifyDialog } from "@/pages/artists/components/link-spotify-dialog";
import { MergedName } from "@/pages/artists/components/merged-name";
import { SpotifyEnrichmentPreview } from "@/pages/artists/components/spotify-enrichment-preview";
import { ArtistViewType } from "@/pages/artists/schemas/artist";
import { ArtistSpotifyEnrichmentType } from "@/pages/artists/schemas/artist.create.form";
import { EntityTinaTrace } from "@/pages/tina/components/entity-tina-trace";

const statusVariant: Record<
  ArtistViewType["status"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  Complete: "default",
  InReview: "secondary",
  Incomplete: "outline",
  Unregistered: "destructive",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function ArtistPreview({
  artist,
  onArtistChanged,
  justCreated = false,
}: {
  artist: ArtistViewType;
  onArtistChanged?: () => void;
  /** `?created=1` off the post-create redirect. See `enrichment-async-hint`. */
  justCreated?: boolean;
}) {
  const photo = artist.media.photos[0];
  const navigate = useNavigate();
  const needsReview = artist.enrichmentStatus === "NeedsReview";
  const [spotifyOpen, setSpotifyOpen] = useState(false);
  const [linkSpotifyOpen, setLinkSpotifyOpen] = useState(false);

  const notEnriched =
    !artist.enrichmentStatus || artist.enrichmentStatus === "Unavailable";
  // `Unavailable` is the resting state of every Tina-created artist until its
  // enrichment tail runs, so the "Enrich" label below is the resting label —
  // The button below is a *link* into the review page, not a trigger, so it
  // survives `InProgress`: see
  // `shouldSuppressEnrichmentLink` for the stalled-job case that used to strand
  // an artist behind the hint with nothing to press.
  const hideEnrichmentLink = shouldSuppressEnrichmentLink(
    artist.enrichmentStatus,
    justCreated,
  );

  return (
    <aside className="flex w-1/4 min-w-[260px] flex-col gap-4 border-l border-border bg-card p-5 rounded-l-2xl max-h-[calc(100vh-108px)] sticky top-0 overflow-y-auto">
      {/* Header: photo + name + status */}
      <div className="flex items-start gap-3">
        {photo ? (
          <ZoomablePhoto
            url={photo.url}
            className="rounded-md object-cover w-14 h-14 shrink-0"
          />
        ) : (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <Music className="h-5 w-5" />
          </div>
        )}
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="truncate text-sm font-semibold text-foreground">
            <MergedName name={artist.name} mergedInto={artist.mergedInto} />
          </h2>
          <div className="flex items-center gap-2">
            <Badge variant={statusVariant[artist.status]}>
              {artist.status}
            </Badge>
            {artist.hide ? (
              <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>

      <Separator />

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          {artist.followers.toLocaleString()}
        </span>
        <span className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          {formatDate(artist.createdAt)}
        </span>
      </div>

      {/* Genres */}
      {artist.genres && artist.genres.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Genres
          </span>
          <div className="flex flex-wrap gap-1.5">
            {artist.genres.map((g) => (
              <Badge key={g.id} variant="secondary" className="text-xs">
                {g.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      {artist.tags && artist.tags.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Tags
          </span>
          <div className="flex flex-wrap gap-1.5">
            {artist.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* About */}
      {artist.about && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            About
          </span>
          <p className="line-clamp-4 text-sm leading-relaxed text-foreground">
            {artist.about}
          </p>
        </div>
      )}

      {/* AI Enrichment */}
      {(artist.enrichmentStatus || justCreated) && (
        <>
          <Separator />
          <div className="flex flex-col gap-2">
            <EnrichmentSummary
              resource="artists"
              id={artist.id}
              status={artist.enrichmentStatus}
              enrichment={artist.enrichment}
              featureLabels={ARTIST_FEATURE_LABELS}
              showLink={false}
            />
            <EnrichmentAsyncHint
              status={artist.enrichmentStatus}
              justCreated={justCreated}
            />

            {!hideEnrichmentLink && (
              <Button
                size="sm"
                variant={needsReview ? "default" : "outline"}
                className={cn("w-full gap-1.5", needsReview && "animate-pulse")}
                onClick={() => navigate(`/artists/enrichment/${artist.id}`)}
              >
                <FlaskConical className="h-3.5 w-3.5" />
                {needsReview
                  ? "Review Enrichment"
                  : notEnriched
                    ? "Enrich"
                    : "View Enrichment"}
              </Button>
            )}
          </div>
        </>
      )}

      <Separator />

      {/* Social links */}
      {artist.socialLinks && artist.socialLinks?.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Links
          </span>
          <ul className="flex flex-col gap-1">
            {artist.socialLinks!.map((link) => (
              <li key={link.url}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ExternalLink className="h-3 w-3" />
                  {link.type}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Metadata + Spotify link */}
      <Separator />
      {(artist.metadata?.source || artist.metadata?.spotifyId) && (
        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
          {artist.metadata?.source && (
            <span>
              Source:{" "}
              <span className="text-foreground">{artist.metadata.source}</span>
            </span>
          )}
          {artist.metadata?.spotifyId && (
            <span>
              Spotify ID:{" "}
              <span className="font-mono text-foreground">
                {artist.metadata.spotifyId}
              </span>
            </span>
          )}
        </div>
      )}
      {artist.spotifyEnrichment && (
        <Button
          size="sm"
          variant="outline"
          className="w-full gap-1.5"
          onClick={() => setSpotifyOpen(true)}
        >
          View Spotify Enrichment
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        className="w-full gap-1.5"
        onClick={() => setLinkSpotifyOpen(true)}
      >
        <Music className="h-3.5 w-3.5" />
        {artist.metadata?.spotifyId ? "Re-link Spotify" : "Link Spotify"}
      </Button>

      {/* Which Tina run created this artist, or which job last enriched it. */}
      <EntityTinaTrace
        entityType="artist"
        entityId={artist.id}
        metadata={artist.metadata}
        enrichment={artist.enrichment}
      />

      {/* ID + createdBy footer */}
      <div className="mt-auto flex flex-col gap-0.5 text-xs text-muted-foreground">
        <span className="truncate font-mono">{artist.id}</span>
        {artist.slug && (
          <span>
            slug:{" "}
            <span className="font-mono text-foreground">{artist.slug}</span>
          </span>
        )}
        {artist.createdBy && <span>by {artist.createdBy}</span>}
      </div>

      {/* Spotify Enrichment Dialog */}
      {artist.spotifyEnrichment && (
        <Dialog open={spotifyOpen} onOpenChange={setSpotifyOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Spotify Enrichment — {artist.name}</DialogTitle>
            </DialogHeader>
            <SpotifyEnrichmentPreview
              info={{
                name: artist.name,
                spotifyId: artist.metadata?.spotifyId ?? "",
              }}
              spotifyEnrichment={
                artist.spotifyEnrichment as ArtistSpotifyEnrichmentType
              }
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Link / re-link Spotify dialog */}
      <LinkSpotifyDialog
        artistId={artist.id}
        artistName={artist.name}
        currentSpotifyId={artist.metadata?.spotifyId ?? undefined}
        open={linkSpotifyOpen}
        onOpenChange={setLinkSpotifyOpen}
        onLinked={() => onArtistChanged?.()}
      />
    </aside>
  );
}
